import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ShieldAlert, AlertTriangle, ShieldCheck } from "lucide-react";

const CLINICAL_SAFETY_KEY = "rheumhub_clinical_safety_ack_v2";

export function ClinicalSafetyModal() {
  const [show, setShow] = useState(false);
  const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");

  useEffect(() => {
    const acked = localStorage.getItem(CLINICAL_SAFETY_KEY);
    if (!acked) setShow(true);
  }, []);

  if (!show) return null;

  const handleAcknowledge = () => {
    localStorage.setItem(CLINICAL_SAFETY_KEY, "1");
    setShow(false);
  };

  const appName = isAdvicelineHub ? "Advice Line Hub" : "RheumHub";
  const safetyEmail = "safety@medantixhealth.co.uk";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full flex flex-col max-h-[calc(100vh-2rem)]">
        <div className="p-6 pb-4 flex items-center gap-3 shrink-0">
          <div className="p-2 rounded-xl bg-amber-50">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Clinical Safety Acknowledgement</h2>
            <p className="text-xs text-slate-500">Required before accessing {appName} — please read carefully</p>
          </div>
        </div>

        <div className="overflow-y-auto px-6 space-y-3 flex-1 min-h-0">
          <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
            <div className="flex gap-2.5">
              <ShieldCheck className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p>
                <strong>{isAdvicelineHub
                  ? "Clinical communication platform."
                  : "Decision-support tool only."}</strong>
                {" "}{appName} is {isAdvicelineHub
                  ? "an advice line communication and triage platform. It is not a medical device and does not provide clinical recommendations, diagnoses, or treatment decisions."
                  : "a clinical decision-support system. It is not a medical device and does not replace clinical judgment, professional accountability, or your regulatory body's standards of practice."}
              </p>
            </div>
            <div className="flex gap-2.5">
              <ShieldCheck className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p><strong>All clinical decisions remain your responsibility.</strong> Always apply your professional training, clinical assessment, and locally agreed protocols. Never rely solely on information from this platform when making decisions about individual patients.</p>
            </div>
            {!isAdvicelineHub && (
              <>
                <div className="flex gap-2.5">
                  <ShieldCheck className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <p><strong>Content currency.</strong> Guideline summaries are based on published sources at the date of last review. Always verify against the primary source before applying to clinical practice.</p>
                </div>
                <div className="flex gap-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <p><strong>AI responses may contain errors.</strong> Do not apply AI output to individual patient care without independent clinical verification.</p>
                </div>
              </>
            )}
            <div className="flex gap-2.5">
              <ShieldCheck className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p><strong>Safety concerns.</strong> Report issues immediately to{" "}
                <a href={`mailto:${safetyEmail}`} className="underline text-amber-700 hover:text-amber-900">{safetyEmail}</a>.
                Our CSO will investigate in accordance with DCB0129.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 leading-relaxed">
            {isAdvicelineHub
              ? "This platform is governed under the NHS Digital DCB0129 clinical safety standard. By continuing you confirm you have read and understood the above."
              : "This platform is governed under the NHS Digital DCB0129 clinical safety standard. By continuing you confirm you have read and understood the above and will use RheumHub only as a supplementary reference tool."}
          </div>
        </div>

        <div className="p-6 pt-4 space-y-2 shrink-0">
          <Button
            onClick={handleAcknowledge}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-xl h-11 font-medium"
          >
            I acknowledge — continue to {appName}
          </Button>
          <p className="text-center text-xs text-slate-400">
            This notice appears once per device.{" "}
            <a href="/clinical-safety" className="underline hover:text-slate-600">Clinical safety information</a>
          </p>
        </div>
      </div>
    </div>
  );
}
